import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowRightLeft, Package, Undo2 } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useData } from "@/context/DataContext";
import { fmtDate, isoDate } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";

const SEL = "h-9 w-full border bg-white px-2 text-sm";
const F = ({ label, children }) => <label className="block text-sm"><span className="micro-label mb-1 block">{label}</span>{children}</label>;

const NewLoan = ({ onDone }) => {
  const { employees, visibleCompanies, companies, nameOf } = useData();
  const [f, setF] = useState({ employee_id: "", to_company_id: "", date_from: isoDate(new Date()), date_to: isoDate(new Date()), notes: "" });
  const techs = employees.filter((e) => e.active !== false && visibleCompanies.some((c) => c.id === e.company_id));
  const emp = techs.find((e) => e.id === f.employee_id);
  const save = async () => {
    try { await api.post("/loans", f); toast.success("Prestito registrato"); onDone(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="grid gap-3 border bg-white p-4 md:grid-cols-5" data-testid="loan-form">
      <F label="Tecnico"><select className={SEL} value={f.employee_id} onChange={(e) => setF({ ...f, employee_id: e.target.value })} data-testid="loan-employee">
        <option value="">Scegli…</option>{techs.map((e) => <option key={e.id} value={e.id}>{e.last_name} {e.first_name} · {nameOf("companies", e.company_id, "code")}</option>)}</select></F>
      <F label="Presta a"><select className={SEL} value={f.to_company_id} onChange={(e) => setF({ ...f, to_company_id: e.target.value })} data-testid="loan-to-company">
        <option value="">Scegli…</option>{companies.filter((c) => c.id !== emp?.company_id).map((c) => <option key={c.id} value={c.id}>{c.legal_name}</option>)}</select></F>
      <F label="Dal"><Input type="date" value={f.date_from} onChange={(e) => setF({ ...f, date_from: e.target.value })} data-testid="loan-date-from" /></F>
      <F label="Al"><Input type="date" value={f.date_to} onChange={(e) => setF({ ...f, date_to: e.target.value })} data-testid="loan-date-to" /></F>
      <div className="flex items-end"><Button className="w-full" disabled={!f.employee_id || !f.to_company_id} onClick={save} data-testid="loan-save"><ArrowRightLeft className="mr-2 h-4 w-4" />Presta tecnico</Button></div>
    </div>
  );
};

const LoadForm = ({ loan, onDone }) => {
  const [whs, setWhs] = useState([]);
  const [items, setItems] = useState([]);
  const [f, setF] = useState({ from_warehouse_id: "", item_id: "", qty: 1, serials: "" });
  useEffect(() => {
    api.get("/warehouse/warehouses").then((r) => setWhs(r.data.filter((w) => w.company_id === loan.to_company_id && w.kind === "CENTRAL"))).catch(() => {});
    api.get("/warehouse/items").then((r) => setItems(r.data)).catch(() => {});
  }, [loan]);
  const item = items.find((i) => i.id === f.item_id);
  const load = async () => {
    try {
      await api.post(`/loans/${loan.id}/load`, { ...f, qty: Number(f.qty), serials: item?.serialized ? f.serials.split(/[\s,;]+/).filter(Boolean) : [] });
      toast.success("Materiale caricato sul furgone"); setF({ ...f, item_id: "", serials: "" }); onDone();
    } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="grid gap-2 border p-3 md:grid-cols-4" data-testid="loan-load-form">
      <select className={SEL} value={f.from_warehouse_id} onChange={(e) => setF({ ...f, from_warehouse_id: e.target.value })} data-testid="loan-load-warehouse"><option value="">Magazzino di origine…</option>{whs.map((w) => <option key={w.id} value={w.id}>{w.name}</option>)}</select>
      <select className={SEL} value={f.item_id} onChange={(e) => setF({ ...f, item_id: e.target.value })} data-testid="loan-load-item"><option value="">Articolo…</option>{items.filter((i) => !i.category?.startsWith("Attrezzatura")).map((i) => <option key={i.id} value={i.id}>{i.code} · {i.name}</option>)}</select>
      {item?.serialized ? <Input placeholder="Seriali separati da spazio" value={f.serials} onChange={(e) => setF({ ...f, serials: e.target.value })} data-testid="loan-load-serials" />
        : <Input type="number" min="1" value={f.qty} onChange={(e) => setF({ ...f, qty: e.target.value })} data-testid="loan-load-qty" />}
      <Button onClick={load} disabled={!f.from_warehouse_id || !f.item_id} data-testid="loan-load-submit"><Package className="mr-2 h-4 w-4" />Carica sul furgone</Button>
    </div>
  );
};

const Materials = ({ loan, onClose }) => {
  const [data, setData] = useState(null);
  const load = useCallback(() => api.get(`/loans/${loan.id}/materials`).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e))), [loan]);
  useEffect(() => { load(); }, [load]);
  const back = async () => {
    try { const r = await api.post(`/loans/${loan.id}/return`); toast.success(r.data.returned.length ? `Rientrati ${r.data.returned.length} articoli` : "Nulla da far rientrare"); load(); } catch (e) { toast.error(errMsg(e)); }
  };
  const live = loan.status === "ACTIVE" && loan.date_to >= isoDate(new Date());
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[88vh] max-w-4xl overflow-y-auto" data-testid="loan-materials">
        <DialogHeader><DialogTitle className="font-display text-2xl">Materiali prestito · {loan.employee_name}</DialogTitle></DialogHeader>
        <p className="text-sm text-muted-foreground">Furgone {(loan.van_name || "—").replace(/^Furgone\s+/i, "")} · {fmtDate(loan.date_from)} – {fmtDate(loan.date_to)}. Il materiale resta della società che riceve il tecnico ed è solo tracciato.</p>
        {live && <LoadForm loan={loan} onDone={load} />}
        <table className="w-full text-sm" data-testid="loan-balance">
          <thead><tr className="border-b text-left text-xs uppercase text-muted-foreground"><th className="py-2">Articolo</th><th className="text-right">Caricato</th><th className="text-right">Usato</th><th className="text-right">Rientrato</th><th className="text-right">Da rientrare</th></tr></thead>
          <tbody>{(data?.rows || []).map((r) => (
            <tr key={r.item_id} className="border-b" data-testid={`loan-balance-${r.code}`}><td className="py-2"><span className="font-mono text-xs">{r.code}</span> {r.name}</td><td className="text-right">{r.loaded}</td><td className="text-right">{r.used}</td><td className="text-right">{r.returned}</td><td className={`text-right font-semibold ${r.balance > 0 ? "text-amber-700" : ""}`}>{r.balance}</td></tr>
          ))}{data && !data.rows.length && <tr><td colSpan={5} className="py-3 text-muted-foreground">Nessun materiale caricato per questo prestito.</td></tr>}</tbody>
        </table>
        <Button variant="outline" onClick={back} disabled={!data?.rows.some((r) => r.balance > 0)} data-testid="loan-return"><Undo2 className="mr-2 h-4 w-4" />Rientro materiale rimasto al magazzino</Button>
      </DialogContent>
    </Dialog>
  );
};

export const LoansPanel = () => {
  const { nameOf, reload } = useData();
  const [rows, setRows] = useState([]);
  const [mat, setMat] = useState(null);
  const load = useCallback(() => api.get("/loans").then((r) => setRows(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const end = async (l) => { try { await api.post(`/loans/${l.id}/end`); toast.success("Prestito chiuso"); load(); reload?.(); } catch (e) { toast.error(errMsg(e)); } };
  const today = isoDate(new Date());
  return (
    <div className="space-y-4" data-testid="loans-panel">
      <p className="text-sm text-muted-foreground">Durante il prestito il tecnico, con squadra e furgone, compare in Pianificazione della società che lo riceve; resta dipendente della sua società. Il lavorato viene rifatturato con il BEF (Amministrazione).</p>
      <NewLoan onDone={() => { load(); reload?.(); }} />
      <div className="overflow-x-auto border bg-white">
        <table className="w-full text-sm">
          <thead><tr className="border-b text-left text-xs uppercase text-muted-foreground"><th className="p-3">Tecnico</th><th>Da</th><th>A</th><th>Periodo</th><th>Stato</th><th>Registrato da</th><th /></tr></thead>
          <tbody>{rows.map((l) => {
            const live = l.status === "ACTIVE" && l.date_to >= today;
            return (
              <tr key={l.id} className="border-b" data-testid={`loan-row-${l.id}`}>
                <td className="p-3 font-semibold">{l.employee_name}<div className="text-xs font-normal text-muted-foreground">{l.van_name}</div></td>
                <td>{nameOf("companies", l.from_company_id, "code")}</td><td>{nameOf("companies", l.to_company_id, "code")}</td>
                <td>{fmtDate(l.date_from)} – {fmtDate(l.date_to)}</td>
                <td><span className={`px-2 py-0.5 text-xs ${live ? "bg-emerald-100 text-emerald-800" : "bg-zinc-100"}`}>{live ? (l.date_from > today ? "Programmato" : "In corso") : "Concluso"}</span></td>
                <td className="text-xs">{l.created_by}</td>
                <td className="space-x-1 whitespace-nowrap p-2 text-right">
                  <Button size="sm" variant="outline" onClick={() => setMat(l)} data-testid={`loan-materials-${l.id}`}>Materiali</Button>
                  {live && <Button size="sm" variant="ghost" onClick={() => end(l)} data-testid={`loan-end-${l.id}`}>Chiudi</Button>}
                </td>
              </tr>
            );
          })}{!rows.length && <tr><td colSpan={7} className="p-4 text-muted-foreground" data-testid="loans-empty">Nessun prestito registrato.</td></tr>}</tbody>
        </table>
      </div>
      {mat && <Materials loan={mat} onClose={() => { setMat(null); load(); }} />}
    </div>
  );
};
